import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'

type BaseType = 'decimal' | 'binary' | 'octal' | 'hex'

interface BaseOption {
  type: BaseType
  nameKey: string
  prefix: string
  radix: number
  color: string
}

function NumberBase() {
  const { t } = useTranslation()
  const [decimal, setDecimal] = useState('')
  const [binary, setBinary] = useState('')
  const [octal, setOctal] = useState('')
  const [hex, setHex] = useState('')
  const [activeBase, setActiveBase] = useState<BaseType>('decimal')
  const [error, setError] = useState('')

  const baseOptions: BaseOption[] = [
    { type: 'decimal', nameKey: 'decimal', prefix: '', radix: 10, color: 'text-blue-500' },
    { type: 'binary', nameKey: 'binary', prefix: '0b', radix: 2, color: 'text-green-500' },
    { type: 'octal', nameKey: 'octal', prefix: '0o', radix: 8, color: 'text-purple-500' },
    { type: 'hex', nameKey: 'hex', prefix: '0x', radix: 16, color: 'text-orange-500' },
  ]

  const updateAllBases = (value: string, fromBase: BaseType) => {
    setError('')
    
    if (!value.trim()) {
      setDecimal('')
      setBinary('')
      setOctal('')
      setHex('')
      return
    }

    try {
      let decimalValue: number

      switch (fromBase) {
        case 'decimal':
          setDecimal(value)
          decimalValue = parseInt(value, 10)
          break
        case 'binary':
          setBinary(value)
          decimalValue = parseInt(value.replace(/^0b/i, ''), 2)
          break
        case 'octal':
          setOctal(value)
          decimalValue = parseInt(value.replace(/^0o/i, ''), 8)
          break
        case 'hex':
          setHex(value)
          decimalValue = parseInt(value.replace(/^0x/i, ''), 16)
          break
        default:
          return
      }

      if (isNaN(decimalValue)) {
        setError(t('toolPages.numberBase.invalidNumber', { base: t(`toolPages.numberBase.${fromBase}`) }))
        return
      }

      if (fromBase !== 'decimal') setDecimal(decimalValue.toString())
      if (fromBase !== 'binary') setBinary(decimalValue.toString(2))
      if (fromBase !== 'octal') setOctal(decimalValue.toString(8))
      if (fromBase !== 'hex') setHex(decimalValue.toString(16).toUpperCase())
    } catch {
      setError(t('toolPages.numberBase.invalidFormat'))
    }
  }

  const handleInputChange = (value: string, base: BaseType) => {
    setActiveBase(base)
    updateAllBases(value, base)
  }

  const clearAll = () => {
    setDecimal('')
    setBinary('')
    setOctal('')
    setHex('')
    setError('')
  }

  const formatBinary = (bin: string): string => {
    if (!bin) return ''
    // Add spaces every 4 digits for readability
    return bin.replace(/(.{4})/g, '$1 ').trim()
  }

  const getValue = (base: BaseType): string => {
    switch (base) {
      case 'decimal': return decimal
      case 'binary': return binary
      case 'octal': return octal
      case 'hex': return hex
      default: return ''
    }
  }

  const getPlaceholder = (base: BaseType): string => {
    switch (base) {
      case 'decimal': return 'e.g., 255'
      case 'binary': return 'e.g., 11111111'
      case 'octal': return 'e.g., 377'
      case 'hex': return 'e.g., FF'
      default: return ''
    }
  }

  return (
    <Layout showBackButton title={t('toolPages.numberBase.title')}>
      <div className="flex flex-col p-4 gap-6">
        {/* Header with Clear button */}
        <div className="flex justify-between items-center">
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            {t('toolPages.numberBase.convertBetween')}
          </p>
          <button 
            onClick={clearAll}
            className="text-xs font-medium text-slate-500 hover:text-primary transition-colors flex items-center gap-1"
          >
            <span className="material-symbols-outlined text-[16px]">backspace</span>
            {t('toolPages.numberBase.clearAll')}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-box">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full shrink-0">
              <span className="material-symbols-outlined text-red-600 dark:text-red-400">error</span>
            </div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Base Inputs */}
        <div className="flex flex-col gap-4">
          {baseOptions.map((base) => (
            <div key={base.type} className="flex flex-col gap-2">
              <div className="flex items-center gap-2 px-1">
                <span className={`text-xs font-semibold uppercase tracking-wider ${base.color}`}>
                  {t(`toolPages.numberBase.${base.nameKey}`)}
                </span>
                {base.prefix && (
                  <span className="text-xs text-slate-400 font-mono">({base.prefix})</span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={getValue(base.type)}
                  onChange={(e) => handleInputChange(e.target.value, base.type)}
                  className={`input font-mono ${activeBase === base.type ? 'ring-2 ring-primary/50 border-primary' : ''}`}
                  placeholder={getPlaceholder(base.type)}
                />
                <CopyButton text={getValue(base.type)} />
              </div>
            </div>
          ))}
        </div>

        {/* Binary with spaces (for readability) */}
        {binary && (
          <div className="flex flex-col gap-2">
            <label className="section-label">{t('toolPages.numberBase.binaryFormatted')}</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formatBinary(binary)}
                readOnly
                className="input font-mono text-sm bg-slate-50 dark:bg-[#0d131a]"
              />
              <CopyButton text={formatBinary(binary)} />
            </div>
          </div>
        )}

        {/* Quick Reference */}
        <div className="flex flex-col gap-3">
          <h3 className="section-label">{t('toolPages.numberBase.quickReference')}</h3>
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="grid grid-cols-4 gap-px bg-slate-200 dark:bg-slate-700 text-xs font-semibold text-slate-500 dark:text-slate-400">
              <div className="bg-slate-50 dark:bg-surface-dark p-2 text-center">Dec</div>
              <div className="bg-slate-50 dark:bg-surface-dark p-2 text-center">Bin</div>
              <div className="bg-slate-50 dark:bg-surface-dark p-2 text-center">Oct</div>
              <div className="bg-slate-50 dark:bg-surface-dark p-2 text-center">Hex</div>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {[0, 1, 2, 4, 8, 10, 15, 16, 32, 64, 128, 255].map((num) => (
                <div key={num} className="grid grid-cols-4 text-sm font-mono">
                  <div className="p-2 text-center text-blue-600 dark:text-blue-400">{num}</div>
                  <div className="p-2 text-center text-green-600 dark:text-green-400">{num.toString(2)}</div>
                  <div className="p-2 text-center text-purple-600 dark:text-purple-400">{num.toString(8)}</div>
                  <div className="p-2 text-center text-orange-600 dark:text-orange-400">{num.toString(16).toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom spacing */}
        <div className="h-8"></div>
      </div>
    </Layout>
  )
}

export default NumberBase
